import React, { useEffect } from 'react';
import { ArcStack } from '@src/core/ui';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { SelectedDrone } from '@src/features/drone/components/selected-drone';
import { TopDronesCarousel } from '@src/features/drone/components/top-drones-carousel';
import { WinchControl } from '@src/features/drone/components/winch-control';
import { EnRouteFlightMode, EnRouteIndicator, EnRouteStatusMessage, JoystickView } from '@src/features/order/pages/take-off-to-en-route-page/components';
import FlightData from '@src/features/drone/components/flight-data';
import { ArcBaseSidebar } from '@src/core/components';
import { MissionDetails } from '@src/features/drone/components/mission-details';
import { useGetOrderQuery } from '@src/features/order/redux';
import { skipToken } from '@reduxjs/toolkit/query';
import EmergencyButtons from '@src/features/drone/components/emergency-control';
import { useFlightProvider } from '@src/features/order/provider';
import { DroneStatusType, useDroneProvider } from "@src/core/features";
import { selectSelectedDrone, selectMissionId, selectManualControlMap } from '@src/features/drone/redux/selectors';
import { setMissionId, setSelectedDrone } from '@src/features/drone/redux/actions';
import { useFlyingDrones } from '@src/features/drone/hooks/useFlyingDrones';
import { useDroneSelection } from '@src/features/drone/hooks/useDroneSelection';
import EnRouteManualControl from '@src/features/drone/pages/drones-page/components/en-route-manual-control';
import { Sections } from '@src/features/drone/pages/drones-page/components/sections';
import { setMapLocation } from '@src/core/controllers';


const DronesSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { drones: allDrones, refetch: refetchDrones, isLoading: isDronesLoading } = useDroneProvider();
  const { drones: idleDrones } = useDroneProvider(DroneStatusType.IDLE);
  const { drones: missionDrones } = useDroneProvider(DroneStatusType.MISSION);
  const selectedFromRedux = useSelector(selectSelectedDrone);
  const paramsMissionId = searchParams.get('missionId');
  const missionId = useSelector(selectMissionId) || selectedFromRedux?.missionId || paramsMissionId;
  const manualControl = useSelector(selectManualControlMap);
  const paramsOrderId = searchParams.get('orderId');
  const { flightData } = useFlightProvider();

  const flyingDrones = useFlyingDrones(missionDrones, {}, flightData);

  // Get the correct selectedDrone (merged with flying data if on mission) and isOnMission
  const { selectedDrone, isOnMission } = useDroneSelection(selectedFromRedux, flyingDrones, missionDrones, idleDrones);

  const orderId = paramsOrderId || selectedDrone?.currentOrderId || null;
  const shouldSkip = orderId == null;


  useEffect(() => {
    const droneSerial = searchParams.get('droneSerial');
    if (!droneSerial && selectedDrone) {
      dispatch(setSelectedDrone(null));
    }
  }, [searchParams, selectedDrone, dispatch]);

  useEffect(() => {
    const droneSerial = searchParams.get('droneSerial');
    const orderIdParam = searchParams.get('orderId');
    
    if (!droneSerial) return;
    
    // Don't try to find drone while still loading initial data
    if (isDronesLoading) return;
    
    // Search in all drone arrays, prioritizing missionDrones, then idleDrones, then allDrones
    const foundDrone = 
      missionDrones.find((drone) => drone.serialNumber === droneSerial) ||
      idleDrones.find((drone) => drone.serialNumber === droneSerial) ||
      allDrones.find((drone) => drone.serialNumber === droneSerial);
    
    // If drone not found and not loading, trigger a refetch (might be a timing issue after take-off)
    if (!foundDrone) {
      refetchDrones();
      return;
    }
    
    // Check if we need to update selected drone
    const currentSerial = selectedFromRedux?.serialNumber;
    const expectedOrderId = orderIdParam ? Number(orderIdParam) : foundDrone.currentOrderId;
    
    const needsUpdate = 
      currentSerial !== droneSerial;
    
    if (needsUpdate) {
      dispatch(setMapLocation({
        latitude: Number(foundDrone.latitude),
        longitude: Number(foundDrone.longitude),
      }));
      dispatch(setSelectedDrone({
        ...foundDrone, 
        currentOrderId: expectedOrderId
      }));
    }
  }, [searchParams, idleDrones, missionDrones, allDrones, selectedFromRedux, dispatch, refetchDrones, isDronesLoading]);
  

  const { data: order, isLoading: isOrderLoading } = useGetOrderQuery(
    shouldSkip ? skipToken : orderId,
    {
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
    }
  ); 
  
  useEffect(() => {
    // If we have missionId from URL params, use it immediately
    if (paramsMissionId) {
      dispatch(setMissionId(paramsMissionId));
      return;
    }
    
    if (!orderId) {
      dispatch(setMissionId(undefined));
      return;
    }

    if (order?.missionId) {
      dispatch(setMissionId(order.missionId));
    }
  }, [orderId, order?.missionId, paramsMissionId, dispatch]);



  return (
    <ArcBaseSidebar>
      <ArcStack>
        {selectedDrone && <TopDronesCarousel idleDrones={idleDrones} flyingDrones={flyingDrones} selectedDrone={selectedDrone} flightData={flightData} />}
        {selectedDrone ? <SelectedDrone drone={selectedDrone} isOnMission={isOnMission} /> : <Sections idleDrones={idleDrones} missionDrones={flyingDrones} />}
        {(selectedDrone && order && orderId) && (
          <MissionDetails order={order} />
        )}
        {isOnMission && selectedDrone && <EmergencyButtons drone={selectedDrone} isLoading={isOrderLoading} />
        }
        {selectedDrone && (
          <ArcStack spacing={2}> 
            <WinchControl droneId={selectedDrone.id.toString()} />
            <ArcStack spacing={1} direction='row' justifyItems={'center'} alignItems={'center'}>
              <FlightData order={order} droneSerial={selectedDrone?.serialNumber} />
              <EnRouteIndicator />
            </ArcStack>

            <EnRouteStatusMessage missionId={missionId} />
            <EnRouteFlightMode missionId={missionId} />
            <EnRouteManualControl drone={selectedDrone} />
            <JoystickView 
              drone={selectedDrone} 
              isManualControl={manualControl[selectedDrone?.serialNumber]} />
          </ArcStack>
        )}

      </ArcStack>
    </ArcBaseSidebar>
  );
};

export default DronesSidebar;